import { useState, type ReactNode } from 'react';
import { api, ApiClientError } from '../api/client.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * Step-up, as a question rather than an error.
 *
 * `auth.step_up_required` is not a failure: the session is valid and the
 * permission is held. The API is asking the person to prove they are still the
 * person, before something that moves money or reveals a credential. Reporting
 * it as an error message is a dead end — the user is told to re-verify with
 * nothing to re-verify with.
 *
 * That dead end was live: SipCredential asked properly and retried, while the
 * balance-adjustment form printed "Re-verify your identity to continue" and
 * offered no way to do it. One of the two was right, so it is generalised here
 * rather than copied (ADR-019) and both now use it.
 *
 * It became reachable at all because logging in used to stamp `sessions.mfa_at`,
 * which satisfied the five-minute window without anybody ever being asked. The
 * server no longer does that, so this is now the only path through.
 *
 *   const step = useStepUp();
 *   step.run(() => adjust.mutateAsync(body));    // prompts and retries by itself
 *   ...
 *   {step.challenge && <StepUpPrompt step={step} what="Adjusting a balance" />}
 */

const STEP_UP = 'auth.step_up_required';

export interface StepUp {
  /** True while the password is being asked for. */
  challenge: boolean;
  busy: boolean;
  error: string | null;
  password: string;
  setPassword: (v: string) => void;
  /** Runs the action; on a step-up refusal, holds it and raises the challenge. */
  run: <T>(action: () => Promise<T>) => Promise<T | undefined>;
  /** Verifies the password and replays the held action. */
  verify: () => Promise<void>;
  cancel: () => void;
}

export function useStepUp(): StepUp {
  const [challenge, setChallenge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  /**
   * The action is held in state, not re-derived, so the replay after
   * verification is the SAME call with the same arguments. Asking someone to
   * prove who they are and then making them fill the form in again is how a
   * security control becomes the thing people route around.
   */
  const [pending, setPending] = useState<null | (() => Promise<unknown>)>(null);

  const run = async <T,>(action: () => Promise<T>): Promise<T | undefined> => {
    setError(null);
    try {
      return await action();
    } catch (e) {
      if (e instanceof ApiClientError && e.code === STEP_UP) {
        setPending(() => action);
        setChallenge(true);
        return undefined;
      }
      throw e;
    }
  };

  const verify = async () => {
    setBusy(true); setError(null);
    try {
      await api.post('/v1/auth/step-up', { password });
      const held = pending;
      setPassword(''); setChallenge(false); setPending(null);
      if (held) await held();
    } catch (e) {
      setError(e instanceof ApiClientError && e.status === 401
        ? 'That password is not correct'
        : 'Could not verify your identity');
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setChallenge(false); setPending(null); setPassword(''); setError(null);
  };

  return { challenge, busy, error, password, setPassword, run, verify, cancel };
}

export function StepUpPrompt({ step, what, children }: {
  step: StepUp;
  /** What is being confirmed, e.g. "Adjusting a balance". Becomes the sentence. */
  what: string;
  children?: ReactNode;
}) {
  if (!step.challenge) return null;
  return (
    <div className="arc-banner arc-banner-info" style={{ marginBottom: 12 }}>
      <Icon name="warning" size={14} />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Confirm it is you</div>
        <p className="arc-muted" style={{ fontSize: 12.5, margin: '0 0 8px' }}>
          {what} is a sensitive action. Enter your own portal password to
          continue. {children}
        </p>
        <div className="arc-row" style={{ gap: 8 }}>
          <input className="arc-input" type="password" autoFocus
            style={{ maxWidth: 260 }} value={step.password}
            placeholder="Your portal password"
            onChange={(e) => step.setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && step.password) void step.verify();
            }} />
          <button type="button" className="arc-btn arc-btn-primary"
            disabled={step.busy || !step.password} onClick={() => void step.verify()}>
            {step.busy ? 'Checking…' : 'Confirm'}
          </button>
          <button type="button" className="arc-btn arc-btn-ghost" onClick={step.cancel}>
            Cancel
          </button>
        </div>
        {step.error && (
          <div style={{ color: 'var(--arc-danger)', fontSize: 12.5, marginTop: 8 }}>
            {step.error}
          </div>
        )}
      </div>
    </div>
  );
}
